import React from 'react';
import {
  BarChart3,
  CheckCircle2,
  Cloud,
  GitBranch,
  Layers,
  Network,
  Shield,
  Workflow
} from 'lucide-react';

export function ITConsultingHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-500/[0.08] via-transparent to-indigo-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Dashboard técnico */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,270px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(56,189,248,0.3)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-sky-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.dashboardLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[62, 45, 78].map((h, i) => (
            <div key={i} className="rounded-lg bg-sky-500/15 border border-sky-500/20 flex items-end p-1" style={{ height: `${h}px` }}>
              <div className="w-full h-1 rounded-full bg-sky-400/40" />
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <div className="h-2 w-full rounded-full bg-white/10" />
          <div className="h-2 w-4/5 rounded-full bg-white/6" />
        </div>
      </div>

      {/* Arquitetura */}
      <div className="absolute right-0 top-0 w-[min(100%,240px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(99,102,241,0.25)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-indigo-500/10 to-slate-900/80 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Network className="w-4 h-4 text-indigo-400" aria-hidden />
            <span className="text-[10px] font-semibold text-white/70">{mock.architectureLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-1">
            {[Layers, Cloud, GitBranch].map((Icon, i) => (
              <React.Fragment key={i}>
                <div className="w-7 h-7 rounded-lg bg-indigo-500/20 border border-indigo-500/25 flex items-center justify-center shrink-0">
                  <Icon className="w-3 h-3 text-indigo-300" aria-hidden />
                </div>
                {i < 2 && <div className="flex-1 h-px bg-indigo-500/30" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Performance */}
      <div className="absolute left-10 sm:left-16 bottom-32 sm:bottom-36 w-[150px] rounded-xl border border-emerald-500/25 bg-emerald-950/50 backdrop-blur-md px-3 py-2.5 z-30 shadow-lg rotate-[3deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
          <div>
            <p className="text-[9px] font-bold text-emerald-200/95">{mock.performanceLabel}</p>
            <p className="text-[10px] text-emerald-300/70 mt-0.5">+34%</p>
          </div>
        </div>
      </div>

      {/* Segurança checklist */}
      <div className="absolute right-6 sm:right-12 bottom-24 sm:bottom-28 w-[min(100%,190px)] rounded-xl border border-amber-500/25 bg-amber-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-amber-400" aria-hidden />
          <span className="text-[10px] font-bold text-amber-200/95">{mock.securityLabel}</span>
        </div>
        <div className="space-y-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-400/80 shrink-0" aria-hidden />
              <div className="h-1.5 flex-1 rounded-full bg-white/10" />
            </div>
          ))}
        </div>
      </div>

      {/* Mapa de processos / integrações */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-sky-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(56,189,248,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-200/80">{mock.processLabel}</span>
          <Workflow className="w-3.5 h-3.5 text-sky-400" aria-hidden />
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {[Network, Cloud, Layers, GitBranch].map((Icon, i) => (
            <div key={i} className="aspect-square rounded-lg bg-sky-500/15 border border-sky-500/20 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-sky-300" aria-hidden />
            </div>
          ))}
        </div>
        <p className="text-[9px] text-sky-300/70 mt-3 text-center">{mock.integrationsLabel}</p>
      </div>
    </div>
  );
}
